import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaSignOutAlt, FaBell, FaComments } from "react-icons/fa";
import UserChatbox from "../pages/ChatBox/UserChatbox";
import "./Header.css";

const UserHeader = ({ setSidebarOpen }) => {
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState("");
  const [chatboxOpen, setChatboxOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user data.");
        }

        const data = await response.json();
        if (data.user) {
          setUserData(data.user);
        } else {
          throw new Error("Invalid user data.");
        }
      } catch (error) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        navigate("/login");
      }
    };

    fetchUserData();
  }, [navigate]);

  // Fetch notifications for the user
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/chat/user-notifications?userId=${userData?._id}`
        );
        if (!response.ok) throw new Error("Failed to fetch notifications.");
        const data = await response.json();
        setNotifications(data);
      } catch (error) {
        console.error(error);
      }
    };

    if (userData) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 5000);
      return () => clearInterval(interval);
    }
  }, [userData]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Mark notifications as read
  const markNotificationsAsRead = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-read`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({ userId: userData._id }),
      });

      if (!response.ok) throw new Error("Failed to mark notifications as read.");

      setNotifications([]);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleDropdown = () => {
    if (showDropdown && notifications.length > 0) {
      markNotificationsAsRead();
    }
    setShowDropdown(!showDropdown);
  };

  return (
    <header className="dashboard-header">
      <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
        <FaBars />
      </button>

      <h5 className="welcome-text desktop-only">Hello, {userData?.name || "User"}</h5>

      <div className="header-right">
        <div className="notification-container" ref={dropdownRef}>
          <button 
            className="notification-btn tooltip-container" 
            onClick={toggleDropdown}
            aria-label="Notifications"
          >
            <FaBell />
            {notifications.length > 0 && <span className="notification-badge">{notifications.length}</span>}
            <span className="custom-tooltip">New message or file alert</span>
          </button>

          {showDropdown && (
            <div className="notification-dropdown">
              {notifications.length > 0 ? (
                <>
                  {notifications.map((notif, index) => (
                    <div key={index} className="notification-item">
                      {notif.message}
                    </div>
                  ))}
                  <button onClick={markNotificationsAsRead} className="mark-as-read-btn">
                    Mark as Read
                  </button>
                </>
              ) : (
                <div className="notification-item">No new notifications</div>
              )}
            </div>
          )}
        </div>

        <button 
          className="chatbox-btn tooltip-container" 
          onClick={() => setChatboxOpen(!chatboxOpen)}
          aria-label="Open chat"
        >
          <FaComments /> Chat with us!
          <span className="custom-tooltip">For queries, docs, support</span>
        </button>
        <button className="logout-btn desktop-only" onClick={handleLogout}>
          <FaSignOutAlt /> Logout
        </button>
      </div>

      {/* Chatbox */}
      {chatboxOpen && (
        <UserChatbox 
          user={userData} 
          onClose={() => setChatboxOpen(false)} 
        />
      )}
    </header>
  );
};

export default UserHeader;